import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent, Collapse, Divider, CircularProgress, Autocomplete,
  useMediaQuery, useTheme, Alert, Chip,
} from '@mui/material';
import {
  Add, Delete, ExpandMore, ExpandLess, Save, Send, ArrowBack,
  Search as SearchIcon, History, TrendingUp,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import evaluationsApi, { Evaluation, EvaluationItem } from '../services/evaluationService';
import companiesApi, { CompanyListItem, CompanyContact, Company } from '../services/companyService';
import priceListApi, { PriceListItem } from '../services/priceListService';
import { formatApiError } from '../utils/formatApiError';
import { fmtMoney, fmtPct } from '../utils/formatMoney';
import ItemSearchDialog from '../components/ItemSearchDialog';
import PriceHistoryPopover from '../components/PriceHistoryPopover';

const TIER_FIELD_MAP: Record<number, keyof PriceListItem> = {
  1: 'tier1_price', 2: 'tier2_price', 3: 'tier3_price', 4: 'tier4_price', 5: 'tier5_price',
};

const emptyItem = (sortOrder: number, competitor?: string): EvaluationItem => ({
  sort_order: sortOrder,
  dfp_sku: '', description: '', msrp: null, current_price: null,
  recommended_price: null, dfp_cost: null, dfp_unit_gm: null,
  competitor: competitor || '', comp_sku: '', comp_price: null,
  est_quantity: null, requested_price: null,
  ext_price: null, ext_cost: null,
});

function recalcItem(item: EvaluationItem): EvaluationItem {
  const rp = item.recommended_price ?? item.requested_price ?? item.current_price;
  const cost = item.dfp_cost ?? 0;
  const qty = item.est_quantity ?? 0;
  const gm = rp && Number(rp) > 0 ? (Number(rp) - Number(cost)) / Number(rp) : null;
  return {
    ...item,
    recommended_price: rp ?? null,
    dfp_unit_gm: gm != null ? Math.round(gm * 10000) / 10000 : null,
    ext_price: rp != null ? Math.round(Number(rp) * qty * 100) / 100 : null,
    ext_cost: cost ? Math.round(Number(cost) * qty * 100) / 100 : null,
  };
}

const EvaluationFormPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);

  const [selectedCompany, setSelectedCompany] = useState<CompanyListItem | null>(null);
  const [contactId, setContactId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<EvaluationItem[]>([emptyItem(0)]);

  const [companyOptions, setCompanyOptions] = useState<CompanyListItem[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [companyTier, setCompanyTier] = useState<number>(1);

  const [expandedItem, setExpandedItem] = useState<number | null>(0);
  const [itemSearchIdx, setItemSearchIdx] = useState<number | null>(null);
  const [itemSearchInitialQuery, setItemSearchInitialQuery] = useState('');

  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [error, setError] = useState('');

  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [historySku, setHistorySku] = useState('');
  const [historyType, setHistoryType] = useState<'approved' | 'requested'>('approved');

  const companySearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const searchCompanies = useCallback(async (query: string) => {
    setCompanyLoading(true);
    try {
      const params: { search?: string; limit: number } = { limit: 20 };
      if (query) params.search = query;
      const results = await companiesApi.list(params);
      setCompanyOptions(results);
    } catch {
      setCompanyOptions([]);
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  const handleCompanySearchInput = useCallback((value: string) => {
    if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
    companySearchTimer.current = setTimeout(() => searchCompanies(value), 300);
  }, [searchCompanies]);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      evaluationsApi.get(Number(id))
        .then(ev => {
          setEvaluation(ev);
          setContactId(ev.company_contact_id ?? '');
          setDescription(ev.opportunity_description ?? '');
          setCompanyTier(ev.company_pricing_tier);
          setItems(ev.items.length > 0 ? ev.items.map(recalcItem) : [emptyItem(0)]);
          companiesApi.get(ev.company_id).then((c: Company) => {
            setSelectedCompany({ id: c.id, name: c.name, pricing_tier: c.pricing_tier, account_number: c.account_number || '', is_active: c.is_active, contact_count: c.contacts.length });
            setContacts(c.contacts.filter(ct => ct.is_active));
          }).catch(() => {});
        })
        .catch(e => setError(formatApiError(e, 'load')))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleCompanySelect = useCallback(async (company: CompanyListItem | null) => {
    setSelectedCompany(company);
    setContactId('');
    if (!company) {
      setContacts([]);
      setCompanyTier(1);
      return;
    }
    try {
      const c = await companiesApi.get(company.id);
      setContacts(c.contacts.filter(ct => ct.is_active));
      const newTier = c.pricing_tier;
      setCompanyTier(newTier);

      const skusInUse = items.filter(i => i.dfp_sku).map(i => i.dfp_sku!).filter(Boolean) as string[];
      if (skusInUse.length > 0) {
        const freshItems = await priceListApi.batchLookup(skusInUse);
        const freshMap = new Map(freshItems.map(fi => [fi.part_number, fi]));
        const tierField = TIER_FIELD_MAP[newTier] || 'tier1_price';
        setItems(prev =>
          prev.map(item => {
            if (!item.dfp_sku) return item;
            const fi = freshMap.get(item.dfp_sku);
            if (!fi) return item;
            const currentPrice = (fi as any)[tierField] ?? null;
            return recalcItem({
              ...item,
              msrp: fi.us_list_price ?? null,
              current_price: currentPrice,
              dfp_cost: fi.dfp_cost ?? null,
              recommended_price: item.requested_price ?? currentPrice,
            });
          })
        );
      }
    } catch {
      setContacts([]);
    }
  }, [items]);

  const readonly = evaluation != null && !['DRAFT', 'REJECTED'].includes(evaluation.status);

  const handleItemSelected = useCallback((idx: number, selected: PriceListItem) => {
    const tierField = TIER_FIELD_MAP[companyTier] || 'tier1_price';
    const currentPrice = (selected as any)[tierField] ?? null;
    const updated = { ...items[idx] };
    updated.dfp_sku = selected.part_number;
    updated.description = selected.description || selected.item_name || '';
    updated.msrp = selected.us_list_price ?? null;
    updated.current_price = currentPrice;
    updated.dfp_cost = selected.dfp_cost ?? null;
    updated.recommended_price = updated.requested_price ?? currentPrice;
    setItems(prev => {
      const next = [...prev];
      next[idx] = recalcItem(updated);
      return next;
    });
  }, [companyTier, items]);

  const updateItem = (idx: number, field: keyof EvaluationItem, value: any) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[idx], [field]: value };
      if (['requested_price', 'est_quantity', 'recommended_price'].includes(field)) {
        next[idx] = recalcItem(item);
      } else {
        next[idx] = item;
      }
      return next;
    });
  };

  const addItem = () => {
    const lastCompetitor = items.length > 0 ? items[items.length - 1].competitor || '' : '';
    setItems(prev => [...prev, emptyItem(prev.length, lastCompetitor)]);
    setExpandedItem(items.length);
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx).map((item, i) => ({ ...item, sort_order: i })));
  };

  const totals = useMemo(() => {
    const extPrice = items.reduce((s, i) => s + (Number(i.ext_price) || 0), 0);
    const extCost = items.reduce((s, i) => s + (Number(i.ext_cost) || 0), 0);
    const gm = extPrice > 0 ? (extPrice - extCost) / extPrice : null;
    const hasApproved = items.some(i => i.approved_price != null);
    const approvedTotal = hasApproved
      ? items.reduce((s, i) => s + (Number(i.approved_price ?? i.requested_price ?? 0) * Number(i.est_quantity ?? 0)), 0)
      : null;
    return { extPrice, extCost, gm, approvedTotal, hasApproved };
  }, [items]);

  const buildPayload = () => ({
    company_id: selectedCompany!.id,
    company_contact_id: contactId ? Number(contactId) : undefined,
    opportunity_description: description,
    items: items.filter(i => i.dfp_sku).map((i, idx) => ({ ...i, sort_order: idx })),
  });

  const handleSave = async () => {
    if (!selectedCompany) { setError('Please select a company'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await evaluationsApi.create(payload);
        navigate(`/evaluations/${created.id}`, { replace: true });
      } else {
        await evaluationsApi.update(Number(id), payload);
        const updated = await evaluationsApi.get(Number(id));
        setEvaluation(updated);
        setItems(updated.items.map(recalcItem));
      }
    } catch (e: any) {
      setError(formatApiError(e, t('common.save')));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setConfirmSubmit(false);
    if (!selectedCompany) { setError('Please select a company'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await evaluationsApi.create(payload);
        const submitted = await evaluationsApi.submit(created.id);
        navigate(`/evaluations/${created.id}`, { replace: true });
        setEvaluation(submitted);
      } else {
        await evaluationsApi.update(Number(id), payload);
        const submitted = await evaluationsApi.submit(Number(id));
        setEvaluation(submitted);
        setItems(submitted.items.map(recalcItem));
      }
    } catch (e: any) {
      setError(formatApiError(e, 'submit'));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = (type: 'approved' | 'requested', sku: string, anchor: HTMLElement) => {
    setHistoryType(type);
    setHistorySku(sku);
    setHistoryAnchor(anchor);
  };

  const handleSkuQuickSearch = useCallback(async (idx: number, query: string) => {
    if (!query.trim()) return;
    try {
      const results = await priceListApi.search(query.trim(), 2);
      if (results.length === 1) {
        handleItemSelected(idx, results[0]);
      } else {
        setItemSearchInitialQuery(query.trim());
        setItemSearchIdx(idx);
      }
    } catch {
      setItemSearchInitialQuery(query.trim());
      setItemSearchIdx(idx);
    }
  }, [handleItemSelected]);

  const openItemSearch = (idx: number) => {
    setItemSearchInitialQuery('');
    setItemSearchIdx(idx);
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const renderItemCard = (item: EvaluationItem, idx: number) => {
    const isExpanded = expandedItem === idx;
    return (
      <Card key={idx} variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {item.dfp_sku || `Item ${idx + 1}`}
            </Typography>
            {item.ext_price != null && (
              <Chip label={fmtMoney(item.ext_price)} size="small" color="primary" variant="outlined" />
            )}
            {!readonly && (
              <IconButton size="small" onClick={() => removeItem(idx)} sx={{ minWidth: 40, minHeight: 40 }}>
                <Delete fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setExpandedItem(isExpanded ? null : idx)} sx={{ minWidth: 40, minHeight: 40 }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {item.description && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{item.description}</Typography>
          )}

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {readonly ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">DFP SKU</Typography>
                  <Typography variant="body2" fontWeight={500}>{item.dfp_sku || '-'}</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="DFP SKU" size="small" fullWidth
                    value={item.dfp_sku || ''}
                    placeholder="Type SKU + Enter"
                    onChange={e => updateItem(idx, 'dfp_sku', e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSkuQuickSearch(idx, item.dfp_sku || ''); }}
                  />
                  <IconButton onClick={() => openItemSearch(idx)} sx={{ minWidth: 40, minHeight: 40 }}>
                    <SearchIcon />
                  </IconButton>
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">MSRP</Typography>
                  <Typography variant="body2">{fmtMoney(item.msrp)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Current Price</Typography>
                  <Typography variant="body2">{fmtMoney(item.current_price)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">DFP Cost</Typography>
                  <Typography variant="body2">{fmtMoney(item.dfp_cost)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">GM%</Typography>
                  <Typography variant="body2">{fmtPct(item.dfp_unit_gm)}</Typography>
                </Box>
              </Box>

              <Divider />
              <Typography variant="caption" fontWeight={600}>Competitor Info</Typography>
              {readonly ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Competitor</Typography>
                    <Typography variant="body2">{item.competitor || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Comp SKU</Typography>
                      <Typography variant="body2">{item.comp_sku || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Comp Price</Typography>
                      <Typography variant="body2">{fmtMoney(item.comp_price)}</Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <TextField label="Competitor" size="small" value={item.competitor || ''}
                    onChange={e => updateItem(idx, 'competitor', e.target.value)} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <TextField label="Comp SKU" size="small" value={item.comp_sku || ''}
                      onChange={e => updateItem(idx, 'comp_sku', e.target.value)} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField label="Comp Price" size="small" type="number" value={item.comp_price ?? ''}
                        onChange={e => updateItem(idx, 'comp_price', e.target.value ? Number(e.target.value) : null)}
                        fullWidth />
                      {item.dfp_sku && (
                        <IconButton size="small" onClick={e => openHistory('requested', item.dfp_sku!, e.currentTarget)} sx={{ minWidth: 36, minHeight: 36 }}>
                          <TrendingUp fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              <Divider />
              <Typography variant="caption" fontWeight={600}>Pricing</Typography>
              {readonly ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Est. Quantity</Typography>
                    <Typography variant="body2">{item.est_quantity ?? '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Requested Price</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmtMoney(item.requested_price)}</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <TextField label="Est. Quantity" size="small" type="number" value={item.est_quantity ?? ''}
                    onChange={e => updateItem(idx, 'est_quantity', e.target.value ? Number(e.target.value) : null)} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField label="Requested Price" size="small" type="number" value={item.requested_price ?? ''}
                      onChange={e => updateItem(idx, 'requested_price', e.target.value ? Number(e.target.value) : null)}
                      fullWidth />
                    {item.dfp_sku && (
                      <IconButton size="small" onClick={e => openHistory('approved', item.dfp_sku!, e.currentTarget)} sx={{ minWidth: 36, minHeight: 36 }}>
                        <History fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ext. Price</Typography>
                  <Typography variant="body2">{fmtMoney(item.ext_price)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ext. Cost</Typography>
                  <Typography variant="body2">{fmtMoney(item.ext_cost)}</Typography>
                </Box>
              </Box>

              {item.approved_price != null && (
                <Box sx={{ mt: 0.5, p: 1, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                  <Typography variant="body2">Approved: {fmtMoney(item.approved_price)}</Typography>
                  {item.item_note && <Typography variant="caption">{item.item_note}</Typography>}
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const renderItemsTable = () => (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            {['Competitor', 'Comp SKU', 'Comp Price', '', 'Qty', 'DFP SKU', '', 'Description', 'MSRP',
              'Current', 'Requested', '', 'Recommended',
              ...(totals.hasApproved ? ['Approved'] : []),
              'DFP Cost', 'GM%', 'Ext Price', 'Ext Cost', ''].map((h, i) => (
              <th key={`${h}-${i}`} style={{ padding: '8px 4px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '4px' }}>
                <TextField size="small" variant="standard" value={item.competitor || ''} disabled={readonly}
                  onChange={e => updateItem(idx, 'competitor', e.target.value)} sx={{ width: 100 }} />
              </td>
              <td style={{ padding: '4px' }}>
                <TextField size="small" variant="standard" value={item.comp_sku || ''} disabled={readonly}
                  onChange={e => updateItem(idx, 'comp_sku', e.target.value)} sx={{ width: 90 }} />
              </td>
              <td style={{ padding: '4px' }}>
                <TextField size="small" variant="standard" type="number" value={item.comp_price ?? ''} disabled={readonly}
                  onChange={e => updateItem(idx, 'comp_price', e.target.value ? Number(e.target.value) : null)} sx={{ width: 70 }} />
              </td>
              <td style={{ padding: '2px' }}>
                {item.dfp_sku && (
                  <IconButton size="small" onClick={e => openHistory('requested', item.dfp_sku!, e.currentTarget)} title="Requested price history">
                    <TrendingUp sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </td>
              <td style={{ padding: '4px' }}>
                <TextField size="small" variant="standard" type="number" value={item.est_quantity ?? ''} disabled={readonly}
                  onChange={e => updateItem(idx, 'est_quantity', e.target.value ? Number(e.target.value) : null)} sx={{ width: 60 }} />
              </td>
              <td style={{ padding: '4px' }}>
                <TextField
                  size="small" variant="standard" value={item.dfp_sku || ''}
                  disabled={readonly}
                  placeholder="SKU + Enter"
                  onChange={e => updateItem(idx, 'dfp_sku', e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSkuQuickSearch(idx, item.dfp_sku || ''); } }}
                  sx={{ width: 140 }}
                />
              </td>
              <td style={{ padding: '2px' }}>
                {!readonly && (
                  <IconButton size="small" onClick={() => openItemSearch(idx)} title="Search item">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </td>
              <td style={{ padding: '4px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.description || '-'}
              </td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.msrp)}</td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.current_price)}</td>
              <td style={{ padding: '4px' }}>
                <TextField size="small" variant="standard" type="number" value={item.requested_price ?? ''} disabled={readonly}
                  onChange={e => updateItem(idx, 'requested_price', e.target.value ? Number(e.target.value) : null)} sx={{ width: 80 }} />
              </td>
              <td style={{ padding: '2px' }}>
                {item.dfp_sku && (
                  <IconButton size="small" onClick={e => openHistory('approved', item.dfp_sku!, e.currentTarget)} title="Approved price history">
                    <History sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.recommended_price)}</td>
              {totals.hasApproved && (
                <td style={{ padding: '4px', fontWeight: 600, color: '#2e7d32' }}>{fmtMoney(item.approved_price)}</td>
              )}
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.dfp_cost)}</td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtPct(item.dfp_unit_gm)}</td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.ext_price)}</td>
              <td style={{ padding: '4px', color: '#888' }}>{fmtMoney(item.ext_cost)}</td>
              <td style={{ padding: '4px' }}>
                {!readonly && items.length > 1 && (
                  <IconButton size="small" onClick={() => removeItem(idx)}><Delete fontSize="small" /></IconButton>
                )}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
            <td colSpan={totals.hasApproved ? 16 : 15} style={{ padding: '8px 4px', textAlign: 'right' }}>Totals:</td>
            <td style={{ padding: '8px 4px' }}>{fmtMoney(totals.extPrice)}</td>
            <td style={{ padding: '8px 4px' }}>{fmtMoney(totals.extCost)}</td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={totals.hasApproved ? 16 : 15} style={{ padding: '4px', textAlign: 'right' }}>GM%:</td>
            <td colSpan={2} style={{ padding: '4px' }}>{fmtPct(totals.gm)}</td>
            <td></td>
          </tr>
          {totals.approvedTotal != null && (
            <>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={totals.hasApproved ? 16 : 15} style={{ padding: '4px', textAlign: 'right', color: '#2e7d32' }}>Approved Total:</td>
                <td style={{ padding: '4px', color: '#2e7d32' }}>{fmtMoney(totals.approvedTotal)}</td>
                <td colSpan={2}></td>
              </tr>
              <tr>
                <td colSpan={totals.hasApproved ? 16 : 15} style={{ padding: '4px', textAlign: 'right', color: totals.approvedTotal - totals.extPrice >= 0 ? '#2e7d32' : '#d32f2f' }}>
                  Difference (Approved - Requested):
                </td>
                <td style={{ padding: '4px', color: totals.approvedTotal - totals.extPrice >= 0 ? '#2e7d32' : '#d32f2f' }}>
                  {totals.approvedTotal - totals.extPrice >= 0 ? '+' : ''}{fmtMoney(totals.approvedTotal - totals.extPrice)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/evaluations')} sx={{ minWidth: 48, minHeight: 48 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {isNew ? t('evaluations.new', 'New Evaluation') : `Evaluation ${evaluation?.evaluation_number || ''}`}
        </Typography>
        {evaluation && (
          <Chip
            label={evaluation.status}
            size="small"
            color={
              evaluation.status === 'APPROVED' ? 'success' :
              evaluation.status === 'SUBMITTED' ? 'warning' :
              evaluation.status === 'REJECTED' ? 'error' : 'default'
            }
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Evaluation Details</Typography>
        {readonly ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Company</Typography>
              <Typography variant="body2" fontWeight={500}>{evaluation?.company_name} (Tier {evaluation?.company_pricing_tier})</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Contact</Typography>
              <Typography variant="body2">{evaluation?.company_contact_name || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Approver</Typography>
              <Typography variant="body2">{evaluation?.approver_name || '-'}</Typography>
            </Box>
            {evaluation?.submitted_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">Submitted</Typography>
                <Typography variant="body2">{new Date(evaluation.submitted_at).toLocaleDateString()}</Typography>
              </Box>
            )}
            {evaluation?.approved_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">{evaluation.status === 'APPROVED' ? 'Approved' : 'Reviewed'}</Typography>
                <Typography variant="body2">{new Date(evaluation.approved_at).toLocaleDateString()}</Typography>
              </Box>
            )}
            {evaluation?.opportunity_description && (
              <Box sx={{ gridColumn: '1 / -1', mt: 0.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Opportunity Description</Typography>
                <Typography variant="body2">{evaluation.opportunity_description}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <Autocomplete
              options={companyOptions}
              getOptionLabel={(o) => `${o.name} (Tier ${o.pricing_tier})`}
              loading={companyLoading}
              value={selectedCompany}
              onChange={(_, v) => handleCompanySelect(v)}
              onInputChange={(_, v, reason) => { if (reason === 'input') handleCompanySearchInput(v); }}
              onOpen={() => { if (companyOptions.length === 0) searchCompanies(''); }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Company" size="small" />}
              filterOptions={(x) => x}
            />

            <Autocomplete
              options={contacts}
              getOptionLabel={(c) => `${c.first_name} ${c.last_name}`}
              value={contacts.find(c => c.id === contactId) ?? null}
              onChange={(_, v) => setContactId(v?.id ?? '')}
              disabled={!selectedCompany}
              renderInput={(params) => <TextField {...params} label="Contact" size="small" />}
            />

            <TextField
              label="Opportunity Description" size="small" fullWidth multiline rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              sx={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}
            />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2">Items</Typography>
          {!readonly && (
            <Button size="small" startIcon={<Add />} onClick={addItem} sx={{ minHeight: 48 }}>
              Add Item
            </Button>
          )}
        </Box>

        {isMobile ? (
          <Box>{items.map((item, idx) => renderItemCard(item, idx))}</Box>
        ) : (
          renderItemsTable()
        )}

        {isMobile && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Total Ext Price:</strong> {fmtMoney(totals.extPrice)}</Typography>
            <Typography variant="body2"><strong>Total Ext Cost:</strong> {fmtMoney(totals.extCost)}</Typography>
            <Typography variant="body2"><strong>GM%:</strong> {fmtPct(totals.gm)}</Typography>
            {totals.approvedTotal != null && (
              <>
                <Typography variant="body2" sx={{ color: '#2e7d32', mt: 0.5 }}><strong>Approved Total:</strong> {fmtMoney(totals.approvedTotal)}</Typography>
                <Typography variant="body2" sx={{ color: totals.approvedTotal - totals.extPrice >= 0 ? '#2e7d32' : '#d32f2f' }}>
                  <strong>Difference:</strong> {totals.approvedTotal - totals.extPrice >= 0 ? '+' : ''}{fmtMoney(totals.approvedTotal - totals.extPrice)}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Paper>

      {!readonly && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined" startIcon={<Save />} onClick={handleSave}
            disabled={saving} sx={{ minHeight: 48, flex: isMobile ? 1 : 'none' }}
          >
            {t('common.save', 'Save Draft')}
          </Button>
          <Button
            variant="contained" startIcon={<Send />}
            onClick={() => setConfirmSubmit(true)}
            disabled={saving || items.filter(i => i.dfp_sku).length === 0}
            sx={{ minHeight: 48, flex: isMobile ? 1 : 'none' }}
          >
            Submit for Approval
          </Button>
        </Box>
      )}

      <Dialog open={confirmSubmit} onClose={() => setConfirmSubmit(false)}>
        <DialogTitle>Submit Evaluation?</DialogTitle>
        <DialogContent>
          <Typography>
            This will send the evaluation to your approver for review. You won't be able to edit it until it's approved or rejected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmit(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>

      <ItemSearchDialog
        open={itemSearchIdx !== null}
        onClose={() => setItemSearchIdx(null)}
        onSelect={(item) => { if (itemSearchIdx !== null) handleItemSelected(itemSearchIdx, item); }}
        companyTier={companyTier}
        initialQuery={itemSearchInitialQuery}
      />

      <PriceHistoryPopover
        type={historyType}
        anchorEl={historyAnchor}
        sku={historySku}
        onClose={() => setHistoryAnchor(null)}
      />
    </Box>
  );
};

export default EvaluationFormPage;
